import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BackToDashboard from '../components/BackToDashboard';
import { adminListings, type AdminStats } from '../api/client';

export default function AdminReports() {
  const { t } = useTranslation();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminListings.getStats()
      .then(setStats)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load stats'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="dash-page">
        <BackToDashboard />
        <h1 className="dash-page-title">{t('reports.title')}</h1>
        <p style={{ color: '#6b7280' }}>{t('common.loading')}</p>
      </div>
    );
  }

  if (error || !stats) {
    const is401 = error?.includes('401') || error?.toLowerCase().includes('unauthorized');
    return (
      <div className="dash-page">
        <BackToDashboard />
        <h1 className="dash-page-title">{t('reports.title')}</h1>
        <div className="dash-alert dash-alert--error">{error || 'No data'}</div>
        {is401 && (
          <p style={{ color: '#6b7280', fontSize: '0.9375rem' }}>
            {t('dashboard.sessionInvalid')} {t('common.signIn')} to view reports.
          </p>
        )}
      </div>
    );
  }

  const statusEntries = Object.entries(stats.listingsByStatus);
  const typeEntries = Object.entries(stats.listingsByType);
  const maxStatus = Math.max(1, ...statusEntries.map(([, c]) => c));
  const maxType = Math.max(1, ...typeEntries.map(([, c]) => c));
  const barColors: Record<string, string> = { draft: '#94a3b8', pending_review: '#f59e0b', published: '#22c55e', unpublished: '#ef4444', rent: '#3b82f6', sale: '#8b5cf6', short_term: '#06b6d4' };

  const statusTotal = statusEntries.reduce((s, [, c]) => s + c, 0) || 1;
  const typeTotal = typeEntries.reduce((s, [, c]) => s + c, 0) || 1;
  const statusSegments = statusEntries.reduce<{ deg: number; color: string }[]>((acc, [k, c]) => {
    const deg = acc.length ? acc[acc.length - 1].deg + (c / statusTotal) * 360 : (c / statusTotal) * 360;
    acc.push({ deg, color: barColors[k] ?? '#6366f1' });
    return acc;
  }, []);
  const statusConic = statusSegments.map(({ deg }, i, arr) => `${arr[i].color} ${i === 0 ? 0 : arr[i - 1].deg}deg ${deg}deg`).join(', ');
  const typeSegments = typeEntries.reduce<{ deg: number; color: string }[]>((acc, [k, c]) => {
    const deg = acc.length ? acc[acc.length - 1].deg + (c / typeTotal) * 360 : (c / typeTotal) * 360;
    acc.push({ deg, color: barColors[k] ?? '#6366f1' });
    return acc;
  }, []);
  const typeConic = typeSegments.map(({ deg }, i, arr) => `${arr[i].color} ${i === 0 ? 0 : arr[i - 1].deg}deg ${deg}deg`).join(', ');

  const publishRate = stats.totalListings ? Math.round((stats.listingsByStatus.published / stats.totalListings) * 100) : 0;
  const leadsPerListing = stats.totalListings ? (stats.totalLeads / stats.totalListings).toFixed(1) : '0';
  const viewsPerListing = stats.totalListings ? (stats.totalViews / stats.totalListings).toFixed(0) : '0';
  const topType = typeEntries.length ? typeEntries.reduce((a, b) => (a[1] >= b[1] ? a : b))[0] : '—';

  return (
    <div className="dash-page">
      <BackToDashboard />
      <header className="dash-page-header">
        <h1 className="dash-page-title">{t('reports.title')}</h1>
        <p className="dash-page-desc">{t('reports.subtitle')}</p>
      </header>

      <div className="dash-stats">
        <div className="dash-stat-card">
          <div className="dash-stat-label">{t('reports.totalListings')}</div>
          <div className="dash-stat-value">{stats.totalListings}</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">{t('reports.totalLeads')}</div>
          <div className="dash-stat-value">{stats.totalLeads}</div>
        </div>
        <div className="dash-stat-card">
          <div className="dash-stat-label">{t('reports.totalViews')}</div>
          <div className="dash-stat-value">{stats.totalViews}</div>
        </div>
      </div>

      <div className="dash-kpi-row">
        <div className="dash-kpi-cell">
          <div className="value">{publishRate}%</div>
          <div className="label">Publish rate</div>
        </div>
        <div className="dash-kpi-cell">
          <div className="value">{leadsPerListing}</div>
          <div className="label">Avg leads per listing</div>
        </div>
        <div className="dash-kpi-cell">
          <div className="value">{viewsPerListing}</div>
          <div className="label">Avg views per listing</div>
        </div>
        <div className="dash-kpi-cell">
          <div className="value" style={{ textTransform: 'capitalize', fontSize: '1.125rem' }}>{topType.replace('_', ' ')}</div>
          <div className="label">Top listing type</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="dash-insight-card">
          <h3>Listings</h3>
          <p>{stats.totalListings} total — {stats.listingsByStatus.published} live, {stats.listingsByStatus.draft + stats.listingsByStatus.pending_review} in progress</p>
        </div>
        <div className="dash-insight-card">
          <h3>Leads</h3>
          <p>{stats.totalLeads} inquiries from listing pages and contact forms</p>
        </div>
        <div className="dash-insight-card">
          <h3>Views</h3>
          <p>{stats.totalViews} listing views (engagement metric)</p>
        </div>
        <div className="dash-insight-card">
          <h3>Mix</h3>
          <p>Rent: {stats.listingsByType.rent} · Sale: {stats.listingsByType.sale} · Short-term: {stats.listingsByType.short_term}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <section className="dash-card">
          <div className="dash-card-header">
            <h2 className="dash-card-title">Listing status (pie)</h2>
          </div>
          <div className="dash-card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div className="dash-donut-wrap">
              <div className="dash-donut" style={{ background: statusTotal ? `conic-gradient(${statusConic})` : '#f3f4f6' }}>
                <div className="dash-donut-hole" />
              </div>
              <ul className="dash-donut-legend">
                {statusEntries.map(([status, count]) => (
                  <li key={status}>
                    <span className="dot" style={{ background: barColors[status] ?? '#6366f1' }} />
                    <span className="label" style={{ textTransform: 'capitalize' }}>{status.replace('_', ' ')}</span>
                    <span className="value">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
        <section className="dash-card">
          <div className="dash-card-header">
            <h2 className="dash-card-title">Listing type (pie)</h2>
          </div>
          <div className="dash-card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-start', justifyContent: 'center' }}>
            <div className="dash-donut-wrap">
              <div className="dash-donut" style={{ background: typeTotal ? `conic-gradient(${typeConic})` : '#f3f4f6' }}>
                <div className="dash-donut-hole" />
              </div>
              <ul className="dash-donut-legend">
                {typeEntries.map(([type, count]) => (
                  <li key={type}>
                    <span className="dot" style={{ background: barColors[type] ?? '#6366f1' }} />
                    <span className="label">{type === 'rent' ? t('common.rent') : type === 'sale' ? t('common.buy') : t('common.shortTerm')}</span>
                    <span className="value">{count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
        <section className="dash-card">
          <div className="dash-card-header">
            <h2 className="dash-card-title">{t('reports.byStatus')}</h2>
          </div>
          <div className="dash-card-body">
            <ul className="dash-bar-chart">
              {statusEntries.map(([status, count]) => (
                <li key={status}>
                  <span className="dash-bar-label" style={{ textTransform: 'capitalize' }}>{status.replace('_', ' ')}</span>
                  <div className="dash-bar-track">
                    <div className="dash-bar-fill" style={{ width: `${(count / maxStatus) * 100}%`, background: barColors[status] ?? '#6366f1' }} />
                  </div>
                  <span className="dash-bar-value">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <section className="dash-card">
          <div className="dash-card-header">
            <h2 className="dash-card-title">{t('reports.byType')}</h2>
          </div>
          <div className="dash-card-body">
            <ul className="dash-bar-chart">
              {typeEntries.map(([type, count]) => (
                <li key={type}>
                  <span className="dash-bar-label">{type === 'rent' ? t('common.rent') : type === 'sale' ? t('common.buy') : t('common.shortTerm')}</span>
                  <div className="dash-bar-track">
                    <div className="dash-bar-fill" style={{ width: `${(count / maxType) * 100}%`, background: barColors[type] ?? '#6366f1' }} />
                  </div>
                  <span className="dash-bar-value">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <Link to="/dashboard/leads" className="dash-btn dash-btn--primary" style={{ textDecoration: 'none' }}>{t('reports.viewLeads')}</Link>
        <Link to="/dashboard/all-listings" className="dash-btn dash-btn--secondary" style={{ textDecoration: 'none' }}>{t('reports.viewAllListings')}</Link>
      </div>
    </div>
  );
}
